import { createContext, useContext, useState, useCallback, useMemo } from "react";
import type { ReactNode } from "react";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import type { AlertColor } from "@mui/material/Alert";

interface SnackbarMessage {
  text: string;
  severity: AlertColor;
}

interface SnackbarContextValue {
  showSuccess: (text: string) => void;
  showError: (text: string) => void;
  showWarning: (text: string) => void;
}

const SnackbarContext = createContext<SnackbarContextValue>({
  showSuccess: () => {},
  showError: () => {},
  showWarning: () => {},
});

export function useSnackbar() {
  return useContext(SnackbarContext);
}

export function SnackbarProvider({ children }: { children: ReactNode }) {
  const [message, setMessage] = useState<SnackbarMessage | null>(null);

  const handleClose = useCallback(() => setMessage(null), []);

  const showSuccess = useCallback(
    (text: string) => setMessage({ text, severity: "success" }),
    [],
  );
  const showError = useCallback(
    (text: string) => setMessage({ text, severity: "error" }),
    [],
  );
  const showWarning = useCallback(
    (text: string) => setMessage({ text, severity: "warning" }),
    [],
  );

  const value = useMemo(
    () => ({ showSuccess, showError, showWarning }),
    [showSuccess, showError, showWarning],
  );

  return (
    <SnackbarContext.Provider value={value}>
      {children}
      <Snackbar
        open={message !== null}
        autoHideDuration={5000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {message ? (
          <Alert
            onClose={handleClose}
            severity={message.severity}
            variant="filled"
            sx={{ width: "100%" }}
          >
            {message.text}
          </Alert>
        ) : undefined}
      </Snackbar>
    </SnackbarContext.Provider>
  );
}
